import React, { useImperativeHandle, useRef, useState } from "react";
import { formatCurrency } from "@/utils/formatters";
import { Heading } from "@/components/ui/Heading";
import { RadioGroup, Radio } from "@/components/ui/Radio";
import { GiftAidProps } from "./GiftAidProps";
import classes from "./GiftAid.module.scss";
import { useDonationFormStore } from "@/stores";
import { clsx } from "clsx";
import { getAsset } from "@/utils/assets";
import { useTranslation } from "@/hooks/useTranslation";
import { Checkbox } from "../ui/Checkbox";
import { FormControl } from "../ui/FormControl";

const EXTRA_AMOUNT = 0.25;

export function GiftAid(props: GiftAidProps) {
  const { config, className, ref } = props;
  const { isUKTaxPayer, setUKTaxPayer, donationAmount } =
    useDonationFormStore();

  const amount = donationAmount ?? 0;
  const donationFormated = formatCurrency(amount);
  const donationWithExtra = formatCurrency(amount + amount * EXTRA_AMOUNT);
  const [error, setError] = useState<string | null>(null);
  const firstRadioRef = useRef<HTMLInputElement>(null);

  const t = useTranslation("gift_aid");

  const value =
    typeof isUKTaxPayer === "boolean" ? (isUKTaxPayer ? "yes" : "no") : "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setUKTaxPayer(e.target.value === "yes");
  };

  const hasRadioButtons = config["gift-aid-allow-multiple-selections"];

  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (!hasRadioButtons) return { isValid: true };

      const isValid = typeof isUKTaxPayer === "boolean";
      if (!isValid) {
        if (firstRadioRef.current) {
          firstRadioRef.current.focus();
        }
        setError("Please confirm if we can claim Gift Aid on your donation.");
      } else {
        setError(null);
      }

      return {
        isValid,
      };
    },
  }));

  return (
    <div className={clsx(classes.ukTaxpayer, className)}>
      <div className={classes.header}>
        <Heading gutterBottom>{t(config["gift-aid-title"].i18n)}</Heading>

        <img
          className={classes.logo}
          src={getAsset("/images/giftaid-logo.svg")}
          width={140}
          height={49}
          alt=""
        />
      </div>

      <div className={classes.intro}>
        {t(config["gift-aid-intro"].i18n, {
          amount: donationFormated,
          gift_aid_amount: donationWithExtra,
        })}
      </div>

      <div className={classes.formGroup}>
        {hasRadioButtons && (
          <FormControl type="fieldset" error={error}>
            <RadioGroup
              name="uk-taxpayer"
              value={value}
              direction="column"
              className={classes.radioGroup}
              onChange={handleChange}
            >
              <Radio variant="check" value="yes" ref={firstRadioRef}>
                {t(config["gift-aid-option-yes"].i18n)}
              </Radio>

              <Radio variant="times" value="no">
                {t(config["gift-aid-option-no"]?.i18n ?? "")}
              </Radio>
            </RadioGroup>
          </FormControl>
        )}

        {!hasRadioButtons && (
          <Checkbox
            checked={isUKTaxPayer ?? false}
            onChange={(ev) => setUKTaxPayer(ev.target.checked)}
          >
            {t(config["gift-aid-option-yes"].i18n)}
          </Checkbox>
        )}
      </div>

      <div className={classes.italic}>
        {t(config["gift-aid-post"].i18n, {
          amount: donationWithExtra,
        })}
      </div>
    </div>
  );
}
